import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchParentAccessStatus, fetchParentChildren } from '../../api/guardian';
import { fetchStudentOverview, fetchStudentUpcoming, fetchStudentWallet, fetchChildPlayingPeek } from '../../api/studentPortal';
import { useParentChild } from '../../context/ParentChildContext';
import { useCurrentUser } from '../../context/UserContext';
import { useReportSettings } from '../../context/ReportSettingsContext';
import { isReportEnabled } from '../../config/reportDefinitions';
import ParentChildSwitcher from '../../components/parent/ParentChildSwitcher';
import { formatCurrency } from '../../utils/currency';
import ListPageLayout, { ListLoadingSpinner } from '../../components/layout/ListPageLayout';
import { formatTimeControl } from '../playing/playingUi';
import { ChartPanel, ScoreGauge, CHART_COLORS } from '../../components/charts';
import { apiErrorMessage } from '../../utils/apiError';

const formatRelationship = (value, t) => {
  if (!value) return t('pages.parentHome.guardian');
  const key = String(value).toUpperCase();
  return t(`guardianPublic.relationships.${key}`, {
    defaultValue: String(value)
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase()),
  });
};

const childInitial = (name) => {
  const parts = String(name || '?').trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] || '?').toUpperCase();
};

const StatPill = ({ label, value, loading }) => (
  <div className="rounded-xl bg-white/12 backdrop-blur-sm px-4 py-3 min-w-[7rem]">
    <div className="text-xs font-medium text-slate-300 uppercase tracking-wide">{label}</div>
    <div className="text-xl font-bold text-white mt-0.5 tabular-nums">
      {loading ? '…' : value}
    </div>
  </div>
);

const ParentHome = () => {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const { selectChild, selectedChild, selectedStudentId, approvedChildren } = useParentChild();
  const { settings: reportSettings, ready: reportSettingsReady, loadError: reportSettingsLoadError } = useReportSettings();
  const sessionStatementEnabled = reportSettingsReady && !reportSettingsLoadError && isReportEnabled(reportSettings, 'session-statement');

  const [loading, setLoading] = useState(true);
  const [approved, setApproved] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');
  const [children, setChildren] = useState([]);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotPartialHint, setSnapshotPartialHint] = useState('');
  const [playingPeek, setPlayingPeek] = useState(null);
  const [playingPeekError, setPlayingPeekError] = useState('');
  const [playingPeekLoading, setPlayingPeekLoading] = useState(false);
  const [playingPeekTick, setPlayingPeekTick] = useState(0);

  const statusLabels = useMemo(() => ({
    PENDING_APPROVAL: t('pages.parentHome.statusPendingApproval'),
    APPROVED: t('pages.parentHome.statusApproved'),
    REJECTED: t('pages.parentHome.statusRejected'),
    REVOKED: t('pages.parentHome.statusRevoked'),
  }), [t]);

  const primaryActions = useMemo(() => [
    {
      to: '/parent/dashboard',
      label: t('pages.parentHome.overview'),
      desc: t('pages.parentHome.overviewDesc'),
      gradient: 'from-slate-500 to-slate-600',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      ),
    },
    {
      to: '/parent/calendar',
      label: t('pages.parentHome.calendar'),
      desc: t('pages.parentHome.calendarDesc'),
      gradient: 'from-blue-500 to-slate-600',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      to: '/parent/wallet',
      label: t('pages.parentHome.wallet'),
      desc: t('pages.parentHome.walletDesc'),
      gradient: 'from-emerald-500 to-teal-600',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
    },
    {
      to: '/parent/attendance',
      label: t('pages.parentHome.attendance'),
      desc: t('pages.parentHome.attendanceDesc'),
      gradient: 'from-sky-500 to-blue-600',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ], [t]);

  const secondaryLinks = useMemo(() => [
    { to: '/parent/enrollments', label: t('pages.parentHome.enrollments') },
    { to: '/parent/sessions', label: t('pages.parentHome.sessions') },
    { to: '/parent/notifications', label: t('pages.parentHome.notifications') },
    { to: '/parent/session-statement', label: t('pages.parentHome.sessionStatement'), reportKey: 'session-statement' },
  ], [t]);

  useEffect(() => {
    (async () => {
      try {
        const status = await fetchParentAccessStatus();
        setApproved(!!status.approved);
        setPendingMessage('');
        const list = await fetchParentChildren();
        setChildren(Array.isArray(list) ? list : []);
      } catch (err) {
        if (err?.response?.status === 403) {
          setApproved(false);
          setPendingMessage(
            apiErrorMessage(err, t('pages.parentHome.pendingAccess')),
          );
        } else {
          setPendingMessage(apiErrorMessage(err, t('pages.parentHome.loadFailed')));
        }
        setChildren([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  useEffect(() => {
    if (!selectedStudentId) {
      setSnapshot(null);
      setSnapshotPartialHint('');
      setPlayingPeek(null);
      setPlayingPeekError('');
      return;
    }
    let aborted = false;
    (async () => {
      setSnapshotLoading(true);
      setSnapshotPartialHint('');
      setPlayingPeekLoading(true);
      setPlayingPeekError('');
      try {
        const results = await Promise.allSettled([
          fetchStudentOverview(selectedStudentId),
          fetchStudentUpcoming(selectedStudentId, 7),
          fetchStudentWallet(selectedStudentId),
          fetchChildPlayingPeek(selectedStudentId),
        ]);
        if (aborted) return;
        const overview = results[0].status === 'fulfilled' ? results[0].value : null;
        const upcoming = results[1].status === 'fulfilled' ? results[1].value : [];
        const wallet = results[2].status === 'fulfilled' ? results[2].value : null;
        const playing = results[3].status === 'fulfilled' ? results[3].value : null;
        const upRejected = results[1].status === 'rejected';
        const partialFailed = results.slice(0, 3).some((r) => r.status === 'rejected');
        const upcomingFromOverview = overview?.upcomingSessions7d;
        setSnapshot({
          attendanceRate: overview?.attendanceRate ?? null,
          activeEnrollments: overview?.activeEnrollments ?? null,
          upcoming7d: upcomingFromOverview != null
            ? upcomingFromOverview
            : (upRejected ? null : (Array.isArray(upcoming) ? upcoming.length : 0)),
          walletBalance: wallet?.balance ?? null,
        });
        if (partialFailed) {
          setSnapshotPartialHint(t('pages.parentHome.partialSnapshot', {
            defaultValue: 'Some child snapshot data could not be loaded. Figures may be incomplete — refresh to try again.',
          }));
        }
        if (results[3].status === 'fulfilled') {
          setPlayingPeek(playing);
        } else {
          setPlayingPeek(null);
          setPlayingPeekError(t('pages.parentHome.playingLoadFailed', {
            defaultValue: 'Could not load Playing Platform activity.',
          }));
        }
      } catch {
        if (!aborted) {
          setSnapshot(null);
          setSnapshotPartialHint(t('pages.parentHome.snapshotLoadFailed', {
            defaultValue: 'Could not load child snapshot. Refresh to try again.',
          }));
          setPlayingPeek(null);
        }
      } finally {
        if (!aborted) {
          setSnapshotLoading(false);
          setPlayingPeekLoading(false);
        }
      }
    })();
    return () => { aborted = true; };
  }, [selectedStudentId, t, playingPeekTick]);

  const approvedKids = useMemo(
    () => children.filter((c) => c.status === 'APPROVED'),
    [children],
  );
  const pendingKids = useMemo(
    () => children.filter((c) => c.status !== 'APPROVED'),
    [children],
  );

  const visibleSecondaryLinks = useMemo(
    () => secondaryLinks.filter((item) => !item.reportKey || sessionStatementEnabled),
    [secondaryLinks, sessionStatementEnabled],
  );

  const displayName = user?.firstName || user?.name?.split?.(' ')?.[0] || t('pages.parentHome.there');

  const ensureChildSelected = (studentId) => {
    if (studentId) selectChild(studentId);
  };

  if (loading) {
    return (
      <ListPageLayout>
        <ListLoadingSpinner label={t('pages.parentHome.loading')} />
      </ListPageLayout>
    );
  }

  return (
    <div className="page-shell max-w-5xl mx-auto space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800 text-white shadow-lg p-6 sm:p-8">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10 blur-2xl" aria-hidden="true" />
        <div className="absolute -left-4 bottom-0 w-32 h-32 rounded-full bg-white/5 blur-xl" aria-hidden="true" />
        <div className="relative">
          <p className="text-slate-300 text-sm font-medium mb-1">{t('pages.parentHome.portalLabel')}</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {t('pages.parentHome.hello', { name: displayName })}
          </h1>
          <p className="text-slate-200 mt-2 max-w-xl text-sm sm:text-base">
            {approved
              ? t('pages.parentHome.approvedSubtitle')
              : t('pages.parentHome.pendingSubtitle')}
          </p>
        </div>
      </div>

      {!approved && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 shadow-sm">
          <div className="flex gap-4">
            <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('pages.parentHome.accessPending')}</h2>
              <p className="text-gray-600 mt-1 text-sm leading-relaxed">
                {pendingMessage || t('pages.parentHome.pendingDefault')}
              </p>
            </div>
          </div>
        </div>
      )}

      {approved && approvedKids.length === 0 && children.length === 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center">
          <p className="text-gray-600">{t('pages.parentHome.noLinkedStudents')}</p>
        </div>
      )}

      {approved && approvedKids.length > 0 && (
        <>
          {approvedKids.length > 1 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('pages.parentHome.yourChildren')}</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {approvedKids.map((child) => {
                  const isSelected = selectedStudentId === child.studentId;
                  return (
                    <button
                      key={child.studentId}
                      type="button"
                      onClick={() => selectChild(child.studentId)}
                      className={`flex items-center gap-4 rounded-2xl border p-4 text-left transition-all ${
                        isSelected
                          ? 'border-slate-300 bg-slate-50 shadow-md ring-2 ring-slate-200'
                          : 'border-gray-200 bg-white hover:border-slate-200 hover:shadow-sm'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold shrink-0 ${
                        isSelected ? 'bg-slate-600 text-white' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {childInitial(child.studentName)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{child.studentName}</div>
                        <div className="text-sm text-gray-500">{formatRelationship(child.relationship, t)}</div>
                      </div>
                      {isSelected && (
                        <span className="ml-auto shrink-0 text-xs font-semibold text-slate-700 bg-slate-100 px-2 py-1 rounded-full">
                          {t('pages.parentHome.selected')}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {selectedChild && (
            <div className="rounded-2xl bg-gradient-to-br from-slate-600 to-slate-700 text-white shadow-lg overflow-hidden">
              <div className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center text-2xl font-bold shrink-0">
                      {childInitial(selectedChild.studentName)}
                    </div>
                    <div>
                      <p className="text-slate-300 text-sm">{t('pages.parentHome.currentlyViewing')}</p>
                      <h2 className="text-2xl font-bold">{selectedChild.studentName}</h2>
                      <p className="text-slate-200 text-sm mt-0.5">{formatRelationship(selectedChild.relationship, t)}</p>
                    </div>
                  </div>
                  {approvedKids.length > 1 && (
                    <div className="sm:max-w-xs w-full">
                      <ParentChildSwitcher compact />
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3 mt-6">
                  <StatPill
                    label={t('pages.parentHome.attendance')}
                    loading={snapshotLoading}
                    value={snapshot?.attendanceRate != null ? `${Math.round(snapshot.attendanceRate)}%` : '—'}
                  />
                  <StatPill
                    label={t('pages.studentDashboard.upcoming7d')}
                    loading={snapshotLoading}
                    value={snapshot?.upcoming7d ?? '—'}
                  />
                  <StatPill
                    label={t('pages.parentHome.enrollments')}
                    loading={snapshotLoading}
                    value={snapshot?.activeEnrollments ?? '—'}
                  />
                  <StatPill
                    label={t('pages.parentHome.wallet')}
                    loading={snapshotLoading}
                    value={snapshot?.walletBalance != null ? formatCurrency(snapshot.walletBalance) : '—'}
                  />
                </div>
                {snapshotPartialHint && (
                  <p className="mt-4 text-sm text-amber-100/95 bg-white/10 rounded-xl px-3 py-2" role="status">
                    {snapshotPartialHint}
                  </p>
                )}
              </div>
            </div>
          )}

          {selectedStudentId && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ChartPanel
                title={t('charts.childAttendance', { defaultValue: 'Child attendance' })}
                actionTo="/parent/attendance"
                actionLabel={t('charts.viewHistory', { defaultValue: 'History' })}
                loading={snapshotLoading}
                empty={!snapshotLoading && (snapshot?.attendanceRate == null || Number(snapshot?.attendanceRate) === 0)}
                emptyMessage={t('charts.noAttendanceYet', { defaultValue: 'No attendance recorded yet' })}
                height={200}
              >
                <ScoreGauge
                  value={snapshot?.attendanceRate ?? 0}
                  label={t('pages.parentHome.attendance')}
                  color={CHART_COLORS.success}
                />
              </ChartPanel>
              <ChartPanel
                title={t('charts.playingPeek', { defaultValue: 'Playing activity' })}
                actionTo="/playing"
                actionLabel={t('nav.playingPlatform', { defaultValue: 'Playing' })}
                loading={playingPeekLoading}
                empty={!playingPeekLoading && !playingPeek}
                emptyMessage={playingPeekError || t('charts.noPlayingYet', { defaultValue: 'No playing activity yet' })}
                height={200}
              >
                <ScoreGauge
                  value={playingPeek?.rating ?? playingPeek?.gameRating ?? 0}
                  max={Math.max(2000, Number(playingPeek?.rating ?? playingPeek?.gameRating ?? 1200) + 200)}
                  label={t('charts.childRating', { defaultValue: 'Child rating' })}
                  color={CHART_COLORS.primary}
                />
              </ChartPanel>
            </div>
          )}

          {!selectedStudentId && approvedKids.length > 1 && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
              <p className="text-slate-700 font-medium">{t('pages.parentHome.selectChildPrompt')}</p>
            </div>
          )}

          {selectedStudentId && (
            <>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                    {t('pages.parentHome.playingPlatform', { defaultValue: 'Playing Platform' })}
                  </h2>
                  <Link
                    to={`/playing/players/${selectedStudentId}`}
                    className="text-sm font-semibold text-slate-700 hover:underline"
                  >
                    {t('pages.parentHome.viewPlayProfile', { defaultValue: 'View play profile' })}
                  </Link>
                </div>
                {playingPeekLoading ? (
                  <p className="text-sm text-gray-500">{t('pages.parentHome.loading')}</p>
                ) : playingPeekError ? (
                  <div className="text-sm text-amber-700" role="alert">
                    <p>{playingPeekError}</p>
                    <button
                      type="button"
                      className="mt-2 font-semibold text-amber-900 underline"
                      onClick={() => setPlayingPeekTick((n) => n + 1)}
                    >
                      {t('common.retry', { defaultValue: 'Retry' })}
                    </button>
                  </div>
                ) : (
                  <>
                    {playingPeek?.liveGame?.id && (
                      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
                        <div className="min-w-0 text-sm">
                          <p className="font-semibold text-emerald-900">
                            {t('pages.parentHome.childPlayingLive', { defaultValue: 'Playing live now' })}
                          </p>
                          <p className="text-emerald-800/80 truncate">
                            {playingPeek.liveGame.whiteName}
                            {' vs '}
                            {playingPeek.liveGame.blackName}
                            {formatTimeControl(
                              playingPeek.liveGame.timeControlSeconds,
                              playingPeek.liveGame.incrementMs != null
                                ? Math.round(playingPeek.liveGame.incrementMs / 1000)
                                : 0,
                              playingPeek.liveGame.daysPerMove,
                            ) ? ` · ${formatTimeControl(
                              playingPeek.liveGame.timeControlSeconds,
                              playingPeek.liveGame.incrementMs != null
                                ? Math.round(playingPeek.liveGame.incrementMs / 1000)
                                : 0,
                              playingPeek.liveGame.daysPerMove,
                            )}` : ''}
                          </p>
                        </div>
                        {playingPeek.liveGame.spectatorAllowed === false
                          || String(playingPeek.liveGame.gameType || '').toUpperCase() === 'VS_COMPUTER' ? (
                          <span className="shrink-0 text-xs font-semibold uppercase text-emerald-800/70">
                            {t('playing.privateGame', { defaultValue: 'Private' })}
                          </span>
                          ) : (
                          <Link
                            to={`/playing/spectate/${playingPeek.liveGame.id}`}
                            className="shrink-0 rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-800"
                          >
                            {t('pages.parentHome.watchLive', { defaultValue: 'Watch live' })}
                          </Link>
                          )}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3 text-sm">
                      {(playingPeek?.profile?.ratings || []).slice(0, 3).map((r) => (
                        <div key={r.speedClass} className="rounded-xl bg-slate-50 px-3 py-2 min-w-[6.5rem]">
                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            {t(`playing.speed.${r.speedClass}`, { defaultValue: r.speedClass })}
                          </div>
                          <div className="font-bold tabular-nums text-slate-900">{r.rating ?? '—'}</div>
                        </div>
                      ))}
                      {playingPeek?.profile?.recentAccuracyPct != null && (
                        <div className="rounded-xl bg-slate-50 px-3 py-2 min-w-[6.5rem]">
                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            {t('playing.accuracy', { defaultValue: 'Accuracy' })}
                          </div>
                          <div className="font-bold tabular-nums text-slate-900">
                            {playingPeek.profile.recentAccuracyPct}%
                          </div>
                        </div>
                      )}
                    </div>
                    {(playingPeek?.profile?.recentGames || []).length > 0 && (
                      <ul className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
                        {(playingPeek.profile.recentGames || []).slice(0, 5).map((g) => (
                          <li key={g.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                            <span className="min-w-0 truncate text-slate-800">
                              {g.whiteName}
                              {' vs '}
                              {g.blackName}
                            </span>
                            {(() => {
                              const privateGame = g.spectatorAllowed === false
                                || String(g.gameType || '').toUpperCase() === 'VS_COMPUTER';
                              if (privateGame) {
                                return (
                                  <span className="shrink-0 text-xs font-semibold uppercase text-slate-400">
                                    {t('playing.privateGame', { defaultValue: 'Private' })}
                                  </span>
                                );
                              }
                              const live = g.status === 'ACTIVE' || g.status === 'PENDING';
                              return (
                                <Link
                                  to={`/playing/spectate/${g.id}${live ? '' : '?analyze=1'}`}
                                  className="shrink-0 font-semibold text-slate-700 hover:underline"
                                >
                                  {live
                                    ? t('playing.spectate')
                                    : t('playing.analyze', { defaultValue: 'Analyze' })}
                                </Link>
                              );
                            })()}
                          </li>
                        ))}
                      </ul>
                    )}
                    {!playingPeekLoading && !playingPeekError
                      && !(playingPeek?.profile?.ratings || []).length
                      && !(playingPeek?.profile?.recentGames || []).length
                      && !playingPeek?.liveGame && (
                      <p className="text-sm text-gray-500">
                        {t('pages.parentHome.noPlayingYet', {
                          defaultValue: 'No Playing Platform games yet for this child.',
                        })}
                      </p>
                    )}
                  </>
                )}
              </div>

              <div>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('pages.parentHome.quickActions')}</h2>
                <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
                  {primaryActions.map((action) => (
                    <Link
                      key={action.to}
                      to={action.to}
                      onClick={() => ensureChildSelected(selectedStudentId)}
                      className="group flex flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md hover:border-slate-200 transition-all"
                    >
                      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${action.gradient} text-white flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
                        {action.icon}
                      </div>
                      <div className="font-semibold text-gray-900">{action.label}</div>
                      <p className="text-xs text-gray-500 mt-1 leading-snug">{action.desc}</p>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 px-1">{t('pages.parentHome.more')}</h2>
                <div className="flex flex-wrap gap-2">
                  {visibleSecondaryLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => ensureChildSelected(selectedStudentId)}
                      className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-slate-50 hover:border-slate-200 hover:text-slate-800 transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </>
          )}

          {approvedKids.length === 1 && !selectedStudentId && approvedChildren.length > 0 && (
            <ListLoadingSpinner label={t('pages.parentHome.loadingChild')} />
          )}
        </>
      )}

      {approved && pendingKids.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('pages.parentHome.otherLinks')}</h2>
          <ul className="space-y-2">
            {pendingKids.map((child) => (
              <li key={child.studentId} className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
                <div>
                  <span className="font-medium text-gray-900">{child.studentName}</span>
                  <span className="text-gray-400 mx-2">·</span>
                  <span className="text-sm text-gray-500">{formatRelationship(child.relationship, t)}</span>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 shrink-0">
                  {statusLabels[child.status] || child.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {approved && (
        <p className="text-sm text-gray-500 text-center pb-2">
          <Link to="/parent/faq" className="text-slate-600 hover:text-slate-800 font-medium hover:underline">
            {t('pages.parentHome.helpFaq')}
          </Link>
        </p>
      )}
    </div>
  );
};

export default ParentHome;
